import React, { useEffect, useRef, useState } from 'react';
import { Camera, X as XIcon, RotateCw, Check } from 'lucide-react';

/**
 * Live camera capture modal. Uses getUserMedia (front camera by default,
 * with a "rotate" button to toggle to rear). On capture, emits a `File`
 * (JPEG, 720x720 max) to onCapture(file) and closes.
 *
 * Falls back gracefully if no camera available — parent should keep its
 * own file-input as the alternative.
 */
export default function CameraCapture({ open, onClose, onCapture }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState('user');
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    setReady(false);

    async function start() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: facing, width: { ideal: 720 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => setReady(true);
        }
      } catch (e) {
        const msg = e?.name === 'NotAllowedError'
          ? 'Permiso de cámara denegado. Ve a la configuración del navegador y activa el acceso.'
          : (e?.name === 'NotFoundError' ? 'No se encontró cámara en este dispositivo.' : 'No se pudo acceder a la cámara.');
        setError(msg);
      }
    }
    start();

    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    };
  }, [open, facing]);

  const flip = () => setFacing(f => (f === 'user' ? 'environment' : 'user'));

  const capture = () => {
    const v = videoRef.current;
    if (!v || !ready) return;
    const w = v.videoWidth;
    const h = v.videoHeight;
    const size = Math.min(w, h);
    const sx = (w - size) / 2;
    const sy = (h - size) / 2;
    const canvas = document.createElement('canvas');
    canvas.width = 720;
    canvas.height = 720;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(v, sx, sy, size, size, 0, 0, 720, 720);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `selfie-${Date.now()}.jpg`, { type: 'image/jpeg' });
      onCapture(file);
      onClose();
    }, 'image/jpeg', 0.9);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center p-3"
      data-testid="camera-capture-modal"
      onClick={onClose}
    >
      <div className="bg-card-steel border-2 border-gold/50 rounded-lg p-4 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-h uppercase text-lg text-gold flex items-center gap-2">
            <Camera className="w-5 h-5" /> Tomar foto
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1" aria-label="Cerrar" data-testid="camera-close">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="relative aspect-square bg-navy-deep rounded overflow-hidden mb-3 border border-steel">
          {error ? (
            <div className="absolute inset-0 flex items-center justify-center p-4 text-center text-sm text-red-300">
              {error}
            </div>
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className={`w-full h-full object-cover ${facing === 'user' ? 'scale-x-[-1]' : ''}`}
              data-testid="camera-video"
            />
          )}
          {ready && !error && (
            <div className="absolute inset-x-0 bottom-2 text-center">
              <span className="text-[10px] font-mono uppercase tracking-widest text-gold/80 bg-navy/80 px-2 py-0.5 rounded">
                cámara: {facing === 'user' ? 'frontal' : 'trasera'}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2">
          <button
            onClick={flip}
            disabled={!ready || !!error}
            data-testid="camera-flip"
            className="inline-flex items-center gap-1 px-3 py-2.5 rounded border border-gold/40 text-gold hover:bg-gold/10 disabled:opacity-40 text-xs font-h uppercase"
          >
            <RotateCw className="w-3.5 h-3.5" /> Cambiar
          </button>
          <button
            onClick={capture}
            disabled={!ready || !!error}
            data-testid="camera-capture-btn"
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-semibold hover:bg-gold-light disabled:opacity-40"
          >
            <Check className="w-4 h-4" /> Capturar
          </button>
        </div>
        <p className="text-[10px] text-gray-500 text-center mt-2">
          La foto se recorta a cuadrado 720×720 automáticamente.
        </p>
      </div>
    </div>
  );
}
